import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Input, Modal, Typography, message,
} from 'antd';

import { removeCompanyWhatsAppSender } from '../api';
import {
  Sender, apiError, formatSender, unwrap,
} from './shared';

const { Paragraph, Text } = Typography;

interface Props {
  open: boolean;
  companyId: string;
  companyName?: string;
  sender: Sender;
  onClose: () => void;
  onRemoved: () => void;
}

/**
 * Retire the company's WhatsApp number (ADR-037). Confirmed by typing the
 * number's last four digits.
 */
const SenderRemoveModal: React.FC<Props> = ({
  open, companyId, companyName, sender, onClose, onRemoved,
}) => {
  const [digits, setDigits] = useState('');
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lastFour = String(sender.sender || '').replace(/[^0-9]/g, '').slice(-4);
  const confirmed = digits.trim() === lastFour && lastFour.length === 4;

  useEffect(() => {
    if (!open) return;
    setDigits('');
    setError(null);
  }, [open]);

  const handleRemove = async () => {
    if (!confirmed) return;
    setRemoving(true);
    setError(null);
    try {
      const res = unwrap(await removeCompanyWhatsAppSender(companyId, sender.botmapId)) || {};
      const held = Number(res.heldCancelled || 0);
      const scheduled = Number(res.scheduledCancelled || 0);
      message.success(`WhatsApp number removed · ${held} held and ${scheduled} scheduled messages cancelled`);
      onRemoved();
    } catch (err) {
      setError(apiError(err, 'Failed to remove the WhatsApp number.'));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={`Remove ${formatSender(sender.sender)}?`}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button
          key="remove"
          type="primary"
          danger
          loading={removing}
          disabled={!confirmed}
          onClick={handleRemove}
          data-testid="whatsapp-remove-confirm"
        >
          Remove number
        </Button>,
      ]}
    >
      <div data-testid="whatsapp-remove-modal">
        <Paragraph style={{ marginBottom: 8 }}>Removing the number from {companyName || 'this company'}:</Paragraph>
        <ul style={{ paddingLeft: 20, marginBottom: 16, listStyle: 'disc' }}>
          <li>Candidates&apos; WhatsApp messages to this number stop arriving in GoHire.</li>
          <li>Existing WhatsApp conversations stay readable.</li>
          <li>Held and scheduled WhatsApp messages on this number are cancelled.</li>
          <li>
            <Text strong>Also disconnect the number in Infobip</Text>
            {' '}
            so it isn&apos;t billed. GoHire does not do this for you.
          </li>
        </ul>
        <Paragraph style={{ marginBottom: 8 }}>
          Type the last 4 digits of the number (
          <Text code>{lastFour}</Text>
          ) to confirm.
        </Paragraph>
        <Input
          value={digits}
          onChange={(e) => setDigits(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
          onPressEnter={handleRemove}
          inputMode="numeric"
          maxLength={4}
          placeholder={lastFour.replace(/./g, '•')}
          style={{ width: 120 }}
          autoComplete="off"
          data-testid="whatsapp-remove-digits"
        />
        {error && <Alert type="error" showIcon message={error} style={{ marginTop: 16 }} />}
      </div>
    </Modal>
  );
};

export default SenderRemoveModal;
